/**
 * Подбор предложений сессии.
 *
 * Порядок отбора отражает назначение режима: сначала предложения со словами,
 * которые даются плохо, затем со словами, чей срок повторения наступил, и
 * только потом материал переноса — фразы, ни к какому слабому месту не
 * привязанные (ТЗ FR-EN-10, §6 режим 5, §7.3).
 *
 * Предложения не выдумываются: если для слова нет естественной фразы, слово
 * пропускается, а не превращается в машинную конструкцию (ТЗ §8.3).
 *
 * Внутри своего разряда предложения перебираются по кругу, а не с начала
 * списка. Раньше подбор всегда брал первое подходящее, и человек видел одни и
 * те же пять фраз занятие за занятием, хотя в наборе их шестьдесят четыре:
 * материала было достаточно, а доходила до глаз всегда его голова.
 */

import { createRng } from '@type2x/exercise-generator';
import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import { MASTERED_THRESHOLD } from '@type2x/shared-contracts';
import { getSentences, sentencesForWord, type SentenceEntry } from '@type2x/corpora';
import {
  computePriority,
  isDue,
  rankUnits,
  type LearningConfig,
  type PriorityContext,
} from '@type2x/learning-engine';

import { EMPTY_OBSERVATION } from '@type2x/typing-engine';

import type { SentenceDrill, SentenceReason, SentenceSnapshot } from '../contracts/sentences.js';
import { SENTENCES_PER_SESSION } from '../contracts/sentences.js';

/** Единицы, ради которых имеет смысл искать предложение. */
function candidates(snapshot: SentenceSnapshot, nowMs: number): MasteryUnit[] {
  return Object.values(snapshot.units).filter((unit) => {
    if (unit.kind !== 'word') return false;

    const stat: MasteryStat | undefined = snapshot.stats[unit.id];
    if (stat === undefined || stat.attempts === 0) return false;

    return stat.masteryScore < MASTERED_THRESHOLD || isDue(stat, nowMs);
  });
}

/** Слабое место или наступивший срок повторения. */
function reasonOf(stat: MasteryStat): SentenceReason {
  return stat.masteryScore < MASTERED_THRESHOLD ? 'weak' : 'due';
}

/** Пустое предложение сессии: набор ещё не начинался. */
function drill(text: string, unit: MasteryUnit | null, reason: SentenceReason): SentenceDrill {
  return {
    text,
    unitId: unit?.id ?? null,
    word: unit?.content ?? null,
    reason,
    done: false,
    observed: EMPTY_OBSERVATION,
  };
}

/**
 * Собирает материал сессии.
 *
 * Контекст расчёта приоритета передаётся снаружи: он зависит от раскладки и
 * истории контрольных прогонов, которые знает слой оркестрации, а не режим
 * (docs/ARCHITECTURE.md §5).
 *
 * @param snapshot состояние навыка;
 * @param config конфигурация обучающего алгоритма;
 * @param context общий контекст расчёта приоритета;
 * @param limit сколько предложений взять в сессию;
 * @param seed зерно перебора: одно занятие — один набор, разные занятия —
 *   разные фразы. Пустое зерно оставляет прежний порядок и нужно тестам,
 *   которым важен состав, а не разнообразие.
 */
export function selectSentenceDrills(
  snapshot: SentenceSnapshot,
  config: LearningConfig,
  context: PriorityContext,
  limit: number = SENTENCES_PER_SESSION,
  seed = '',
): SentenceDrill[] {
  const ranked = rankUnits(
    candidates(snapshot, context.nowMs).map((unit) =>
      computePriority(unit, snapshot.stats[unit.id]!, config, context),
    ),
  );

  const drills: SentenceDrill[] = [];
  const used = new Set<string>();
  const rng = seed === '' ? null : createRng(seed);

  for (const result of ranked) {
    if (drills.length >= limit) break;

    const found = pickAround(
      sentencesForWord(snapshot.language, result.unit.content),
      used,
      rng?.nextInt(Number.MAX_SAFE_INTEGER) ?? 0,
    );
    // Слово без естественной фразы просто уступает место следующему.
    if (found === undefined) continue;

    used.add(found.text);
    drills.push(drill(found.text, result.unit, reasonOf(snapshot.stats[result.unit.id]!)));
  }

  // Материал переноса добирает сессию до полной длины: режим доступен и тому,
  // у кого слабых мест не осталось.
  const transfer = getSentences(snapshot.language);
  const start = rng === null ? 0 : rng.nextInt(Math.max(transfer.length, 1));

  for (let step = 0; step < transfer.length; step += 1) {
    if (drills.length >= limit) break;

    const sentence = transfer[(start + step) % transfer.length]!;
    if (used.has(sentence.text)) continue;

    used.add(sentence.text);
    drills.push(drill(sentence.text, null, 'transfer'));
  }

  return drills;
}

/**
 * Первое неиспользованное предложение, начиная со сдвига, по кругу.
 *
 * Круг, а не срез: у слова может быть три фразы, и все три должны когда-нибудь
 * достаться человеку, а не только первая.
 */
function pickAround(
  sentences: readonly SentenceEntry[],
  used: ReadonlySet<string>,
  offset: number,
): SentenceEntry | undefined {
  if (sentences.length === 0) return undefined;
  const start = offset % sentences.length;

  for (let step = 0; step < sentences.length; step += 1) {
    const sentence = sentences[(start + step) % sentences.length]!;
    if (!used.has(sentence.text)) return sentence;
  }
  return undefined;
}
